import { Users, Lock, FileText, DollarSign, Activity, Server, Code, Coins, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { networkStats } from '@/data/blockchainData';
import { TronSocketData } from '@/hooks/useTronSocket';
import NumberTicker from '@/components/ui/number-ticker';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface NetworkStatsGridProps {
  socketData?: TronSocketData;
}

const NetworkStatsGrid = ({ socketData }: NetworkStatsGridProps) => {
  const { t } = useTranslation();
  
  const totalAccounts = socketData?.totalAccounts ?? networkStats.totalAccounts;
  const totalTransactions = socketData?.totalTransactions ?? networkStats.totalTransactions;
  const totalValueLocked = socketData?.totalValueLocked ?? networkStats.totalValueLocked;
  const totalTransferVolume = socketData?.totalTransferVolume ?? networkStats.totalTransferVolume;
  const currentTPS = socketData?.currentTPS ?? networkStats.currentTPS;
  const maxTPS = socketData?.maxTPS ?? networkStats.maxTPS;
  const totalNodes = socketData?.totalNodes ?? networkStats.totalNodes;
  const totalContracts = socketData?.totalContracts ?? networkStats.totalContracts;
  const totalTokens = socketData?.totalTokens ?? networkStats.totalTokens;
  
  const accountsChange24h = socketData?.accountsChange24h ?? networkStats.accountsChange24h;
  const transactionsChange24h = socketData?.transactionsChange24h ?? networkStats.transactionsChange24h;
  const tvlChange24h = socketData?.tvlChange24h ?? networkStats.tvlChange24h;
  const volumeChange24h = socketData?.volumeChange24h ?? networkStats.volumeChange24h;

  const tvlUsd = totalValueLocked * 0.28;

  const stats = [
    {
      icon: Users,
      label: t('stats.totalAccounts'),
      tooltip: t('stats.totalAccountsTooltip'),
      value: totalAccounts,
      suffix: '',
      change: accountsChange24h,
      changePrefix: '+',
      changeSuffix: '',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      icon: Lock,
      label: t('stats.totalValueLocked'),
      tooltip: t('stats.totalValueLockedTooltip'),
      value: tvlUsd / 1e9,
      prefix: '$',
      suffix: 'B',
      decimalPlaces: 2,
      change: tvlChange24h,
      changePrefix: '+',
      changeSuffix: '%',
      color: 'text-success',
      bgColor: 'bg-success/10',
    },
    {
      icon: FileText,
      label: t('stats.totalTransactions'),
      tooltip: t('stats.totalTransactionsTooltip'),
      value: totalTransactions,
      suffix: '',
      change: transactionsChange24h / 1e6,
      changePrefix: '+',
      changeSuffix: 'M',
      color: 'text-chart-3',
      bgColor: 'bg-chart-3/10',
    },
    {
      icon: DollarSign,
      label: t('stats.transferVolume'),
      tooltip: t('stats.transferVolumeTooltip'),
      value: totalTransferVolume / 1e12,
      prefix: '$',
      suffix: 'T',
      decimalPlaces: 2,
      change: volumeChange24h / 1e9,
      changePrefix: '+$',
      changeSuffix: 'B',
      color: 'text-chart-4',
      bgColor: 'bg-chart-4/10',
    },
  ];

  const bottomStats = [
    {
      icon: Activity,
      label: t('stats.currentTps').replace(' TPS', ''),
      tooltip: t('stats.currentTpsTooltip'),
      value: currentTPS,
      secondValue: maxTPS,
      color: 'text-primary',
    },
    {
      icon: Server,
      label: t('stats.totalNodes').replace('Total ', ''),
      tooltip: t('stats.totalNodesTooltip'),
      value: totalNodes,
      color: 'text-success',
    },
    {
      icon: Code,
      label: t('stats.totalContracts').replace('Total ', ''),
      tooltip: t('stats.totalContractsTooltip'),
      value: totalContracts,
      color: 'text-chart-3',
    },
    {
      icon: Coins,
      label: t('stats.totalTokens').replace('Total ', ''),
      tooltip: t('stats.totalTokensTooltip'),
      value: totalTokens,
      color: 'text-chart-4',
    },
  ];

  return (
    <div className="glass-card p-3 sm:p-6">
      <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-4 sm:mb-6">
        {stats.map((stat, index) => (
          <div 
            key={stat.label}
            className="stat-card group p-3 sm:p-4 relative"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <div className={`p-1.5 sm:p-2.5 rounded-lg sm:rounded-xl ${stat.bgColor} transition-transform duration-300 group-hover:scale-110`}>
                <stat.icon className={`w-3.5 sm:w-5 h-3.5 sm:h-5 ${stat.color}`} />
              </div>
              <p className="text-[10px] sm:text-sm text-muted-foreground font-medium line-clamp-1 flex-1">{stat.label}</p>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="p-1 hover:bg-secondary/50 rounded-full transition-colors">
                    <Info className="w-3 sm:w-4 h-3 sm:h-4 text-muted-foreground/60 hover:text-muted-foreground" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-64 text-sm bg-card border border-border shadow-lg z-50">
                  <p>{stat.tooltip}</p>
                </PopoverContent>
              </Popover>
            </div>
            <p className="text-base sm:text-2xl font-bold text-foreground mb-0.5 sm:mb-1">
              {stat.prefix}
              <NumberTicker value={stat.value} duration={2000} decimalPlaces={stat.decimalPlaces} />
              {stat.suffix}
            </p>
            <p className="text-[10px] sm:text-sm text-success flex items-center gap-1 flex-wrap">
              <span className="px-1 sm:px-1.5 py-0.5 bg-success/10 rounded text-[9px] sm:text-xs">24h</span>
              <span className="truncate">
                {stat.changePrefix}
                <NumberTicker value={stat.change} duration={1500} decimalPlaces={stat.decimalPlaces} />
                {stat.changeSuffix}
              </span>
            </p>
          </div>
        ))}
      </div>

      <div className="border-t border-border/50 pt-3 sm:pt-4">
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-4">
          {bottomStats.map((stat) => (
            <Popover key={stat.label}>
              <PopoverTrigger asChild>
                <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-4 py-1.5 sm:py-2 bg-secondary/50 rounded-lg sm:rounded-xl cursor-pointer hover:bg-secondary/70 transition-colors">
                  <stat.icon className={`w-3 sm:w-4 h-3 sm:h-4 ${stat.color} flex-shrink-0`} />
                  <span className="text-[10px] sm:text-sm text-muted-foreground hidden sm:inline">{stat.label}</span>
                  <span className="text-xs sm:text-base font-semibold text-foreground">
                    <NumberTicker value={stat.value} duration={1000} />
                    {stat.secondValue && (
                      <>/<NumberTicker value={stat.secondValue} duration={1000} /></>
                    )}
                  </span>
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-64 text-sm bg-card border border-border shadow-lg z-50">
                <p>{stat.tooltip}</p>
              </PopoverContent>
            </Popover>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NetworkStatsGrid;